import React from 'react';
import { useCart } from '../context/CartContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Minus, Plus, X } from 'lucide-react';
import { formatPrice, buildWhatsAppOrderUrl } from '../mock';

const CartDrawer = () => {
  const { items, isOpen, setIsOpen, removeItem, updateQty, subtotal } = useCart();

  const handleCheckout = () => {
    const url = buildWhatsAppOrderUrl(items);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent side="right" className="bg-[#FFFFFF] w-full sm:max-w-[440px] p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#e5ddce]">
          <SheetTitle className="font-serif text-2xl tracking-widest-2 uppercase text-[#1a1a1a]">Cart · {items.length}</SheetTitle>
          <SheetDescription className="sr-only">Your House of Seya shopping cart drawer</SheetDescription>
        </SheetHeader>

        {/* Items */}
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <div className="font-serif text-2xl text-[#1a1a1a]">Your cart is empty</div>
              <p className="text-sm text-[#7a6a54] mt-2">Discover our new arrivals.</p>
              <button onClick={() => setIsOpen(false)} className="mt-6 text-[11px] tracking-widest-2 uppercase border-b border-[#1a1a1a] pb-1 hover:border-[#999959] hover:text-[#999959]">Continue shopping</button>
            </div>
          ) : (
            <ul className="divide-y divide-[#e5ddce]">
              {items.map(item => {
                const itemKey = item.key || item.id;
                return (
                  <li key={itemKey} className="flex gap-3 sm:gap-4 px-4 sm:px-6 py-5">
                    <img src={item.image} alt={item.name} className="w-16 h-20 sm:w-20 sm:h-24 object-cover bg-[#FFFFFF] flex-shrink-0" />
                    <div className="flex-1 min-w-0 flex flex-col">
                      <div className="flex justify-between gap-2">
                        <div>
                          <div className="font-serif text-[15px] sm:text-[17px] leading-snug line-clamp-2">{item.name}</div>
                          {item.variantName && (
                            <div className="text-[11px] text-[#7a6a54] uppercase tracking-wider mt-0.5">{item.variantName}</div>
                          )}
                        </div>
                        <button onClick={() => removeItem(itemKey)} aria-label="Remove" className="text-[#7a6a54] hover:text-[#1a1a1a] p-2 -m-2 flex-shrink-0"><X size={16} /></button>
                      </div>
                      <div className="mt-auto flex items-center justify-between gap-2">
                        <div className="flex items-center border border-[#d8ceb9]">
                          <button onClick={() => updateQty(itemKey, item.qty - 1)} className="w-9 h-9 flex items-center justify-center hover:bg-[#FFFFFF]"><Minus size={12} /></button>
                          <span className="w-8 text-center text-sm">{item.qty}</span>
                          <button onClick={() => updateQty(itemKey, item.qty + 1)} className="w-9 h-9 flex items-center justify-center hover:bg-[#FFFFFF]"><Plus size={12} /></button>
                        </div>
                        <div className="font-serif text-[15px] sm:text-[17px] whitespace-nowrap">{formatPrice(item.price * item.qty)}</div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-[#e5ddce] px-6 py-5">
            <div className="flex justify-between text-[13px] uppercase tracking-widest-2 mb-4">
              <span>Subtotal</span>
              <span className="font-serif text-xl normal-case">{formatPrice(subtotal)}</span>
            </div>
            <button onClick={handleCheckout} className="w-full bg-[#1a1a1a] text-[#FFFFFF] py-4 text-[11px] tracking-widest-2 uppercase hover:bg-[#3a342b] transition-colors">Check Out</button>
            <p className="text-[10px] text-[#7a6a54] mt-3 text-center italic">Shipping, taxes, and discount codes are calculated at checkout</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
